// @ts-nocheck
/**
 * Marketing Analytics Dashboard Component
 * Comprehensive marketing performance insights with Carbon Charts integration
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Dropdown,
  DatePicker,
  DatePickerInput,
  Stack,
  Heading,
  Tag,
  InlineNotification,
  Loading,
  Modal,
  Select,
  SelectItem,
} from '@carbon/react';
import {
  Download,
  Analytics,
  ArrowUp,
  ArrowDown,
  ChartLine,
  CrowdReport,
  Time,
  Checkmark,
  Warning,
} from '@carbon/icons-react';

// Analytics interfaces
interface ConversionMetrics {
  totalVisitors: number;
  leadGeneration: number;
  signupConversion: number;
  purchaseConversion: number;
  conversionRate: number;
  averageOrderValue: number;
}

interface ABTestResult {
  testId: string;
  testName: string;
  status: 'running' | 'completed' | 'draft';
  variant_a: {
    name: string;
    conversions: number;
    visitors: number;
    conversionRate: number;
  };
  variant_b: {
    name: string;
    conversions: number;
    visitors: number;
    conversionRate: number;
  };
  statisticalSignificance: number;
  confidence: number;
  winner: 'a' | 'b' | 'inconclusive';
  startDate: string;
  endDate?: string;
}

interface VisitorBehavior {
  pageViews: number;
  uniqueVisitors: number;
  averageSessionDuration: number;
  bounceRate: number;
  topPages: Array<{ page: string; views: number; conversionRate: number }>;
  trafficSources: Array<{ source: string; visitors: number; percentage: number }>;
}

interface MarketingROI {
  totalSpend: number;
  totalRevenue: number;
  roi: number;
  costPerAcquisition: number;
  customerLifetimeValue: number;
  profitMargin: number;
}

interface MarketingAnalyticsDashboardProps {
  className?: string;
  timeRange?: string;
  onExportData?: (data: any) => void;
}

const MarketingAnalyticsDashboard: React.FC<MarketingAnalyticsDashboardProps> = ({
  className = '',
  timeRange = 'last_30_days',
  onExportData,
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeRange);
  const [conversionMetrics, setConversionMetrics] = useState<ConversionMetrics | null>(null);
  const [abTestResults, setAbTestResults] = useState<ABTestResult[]>([]);
  const [visitorBehavior, setVisitorBehavior] = useState<VisitorBehavior | null>(null);
  const [marketingROI, setMarketingROI] = useState<MarketingROI | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  // Fetch analytics data
  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedTimeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      // Simulate API calls to fetch analytics data
      // In real implementation, these would be actual API calls
      setTimeout(() => {
        setConversionMetrics({
          totalVisitors: 15420,
          leadGeneration: 3240,
          signupConversion: 1680,
          purchaseConversion: 580,
          conversionRate: 10.9,
          averageOrderValue: 129.5,
        });

        setAbTestResults([
          {
            testId: 'test-001',
            testName: 'Landing Page Headline Test',
            status: 'completed',
            variant_a: { name: 'Original', conversions: 48, visitors: 1200, conversionRate: 4.0 },
            variant_b: { name: 'New Headline', conversions: 72, visitors: 1180, conversionRate: 6.1 },
            statisticalSignificance: 95.8,
            confidence: 99,
            winner: 'b',
            startDate: '2024-01-15',
            endDate: '2024-02-15',
          },
          {
            testId: 'test-002',
            testName: 'CTA Button Color Test',
            status: 'running',
            variant_a: { name: 'Blue Button', conversions: 32, visitors: 845, conversionRate: 3.8 },
            variant_b: { name: 'Orange Button', conversions: 41, visitors: 823, conversionRate: 5.0 },
            statisticalSignificance: 78.2,
            confidence: 85,
            winner: 'inconclusive',
            startDate: '2024-02-01',
          },
        ]);

        setVisitorBehavior({
          pageViews: 45230,
          uniqueVisitors: 18940,
          averageSessionDuration: 4.2,
          bounceRate: 32.8,
          topPages: [
            { page: '/landing', views: 12450, conversionRate: 8.2 },
            { page: '/features', views: 8920, conversionRate: 5.1 },
            { page: '/pricing', views: 6780, conversionRate: 12.4 },
          ],
          trafficSources: [
            { source: 'Organic Search', visitors: 8920, percentage: 47.1 },
            { source: 'Direct', visitors: 4280, percentage: 22.6 },
            { source: 'Social Media', visitors: 3140, percentage: 16.6 },
            { source: 'Paid Ads', visitors: 2600, percentage: 13.7 },
          ],
        });

        setMarketingROI({
          totalSpend: 24500,
          totalRevenue: 75100,
          roi: 206.5,
          costPerAcquisition: 42.3,
          customerLifetimeValue: 385.2,
          profitMargin: 67.4,
        });

        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      setLoading(false);
    }
  };

  const handleExportData = () => {
    const exportData = {
      timeRange: selectedTimeRange,
      conversionMetrics,
      abTestResults,
      visitorBehavior,
      marketingROI,
      exportedAt: new Date().toISOString(),
    };

    if (onExportData) {
      onExportData(exportData);
    }

    setExportModalOpen(false);
  };

  const conversionFunnelData = conversionMetrics
    ? [
        { stage: 'Visitors', count: conversionMetrics.totalVisitors, percentage: 100 },
        {
          stage: 'Leads',
          count: conversionMetrics.leadGeneration,
          percentage: (conversionMetrics.leadGeneration / conversionMetrics.totalVisitors) * 100,
        },
        {
          stage: 'Signups',
          count: conversionMetrics.signupConversion,
          percentage: (conversionMetrics.signupConversion / conversionMetrics.totalVisitors) * 100,
        },
        {
          stage: 'Purchases',
          count: conversionMetrics.purchaseConversion,
          percentage: (conversionMetrics.purchaseConversion / conversionMetrics.totalVisitors) * 100,
        },
      ]
    : [];

  const abTestHeaders = [
    { key: 'testName', header: 'Test Name' },
    { key: 'status', header: 'Status' },
    { key: 'variants', header: 'Variants Performance' },
    { key: 'significance', header: 'Statistical Significance' },
    { key: 'winner', header: 'Winner' },
  ];

  const abTestRows = abTestResults.map(test => ({
    id: test.testId,
    testName: test.testName,
    status: (
      <Tag type={test.status === 'completed' ? 'green' : test.status === 'running' ? 'blue' : 'gray'}>
        {test.status.toUpperCase()}
      </Tag>
    ),
    variants: (
      <Stack gap={2}>
        <Text size="sm">
          A: {test.variant_a.conversionRate}% ({test.variant_a.conversions}/{test.variant_a.visitors})
        </Text>
        <Text size="sm">
          B: {test.variant_b.conversionRate}% ({test.variant_b.conversions}/{test.variant_b.visitors})
        </Text>
      </Stack>
    ),
    significance: `${test.statisticalSignificance}%`,
    winner:
      test.winner === 'inconclusive' ? (
        <Tag type="gray">Running</Tag>
      ) : (
        <Tag type="green">{test.winner === 'a' ? test.variant_a.name : test.variant_b.name}</Tag>
      ),
  }));

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Loading />
        <Text style={{ marginTop: 16 }}>Loading marketing analytics...</Text>
      </div>
    );
  }

  return (
    <div className={`marketing-analytics-dashboard ${className}`} style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Heading size="xl">
              <Analytics style={{ marginRight: '8px' }} />
              Marketing Analytics Dashboard
            </Heading>
            <Text>Comprehensive marketing performance insights and A/B test analysis</Text>
          </div>
          <Stack orientation="horizontal" gap={3}>
            <Select
              id="time-range-select"
              value={selectedTimeRange}
              onChange={e => setSelectedTimeRange(e.target.value)}
              labelText="Time Range"
            >
              <SelectItem value="last_7_days" text="Last 7 Days" />
              <SelectItem value="last_30_days" text="Last 30 Days" />
              <SelectItem value="last_90_days" text="Last 90 Days" />
              <SelectItem value="last_year" text="Last Year" />
            </Select>
            <Button kind="primary" renderIcon={Download} onClick={() => setExportModalOpen(true)}>
              Export Report
            </Button>
          </Stack>
        </div>
      </div>

      {/* Key Metrics Overview */}
      <Grid style={{ marginBottom: 24 }}>
        <Column sm={2} md={2} lg={2}>
          <Tile>
            <Stack gap={2}>
              <ArrowUp size={24} style={{ color: '#0f62fe' }} />
              <Heading size="lg">{conversionMetrics?.totalVisitors.toLocaleString()}</Heading>
              <Text>Total Visitors</Text>
              <Text size="sm" style={{ color: '#525252' }}>
                +15.2% vs last period
              </Text>
            </Stack>
          </Tile>
        </Column>
        <Column sm={2} md={2} lg={2}>
          <Tile>
            <Stack gap={2}>
              <ChartLine size={24} style={{ color: '#24a148' }} />
              <Heading size="lg">{conversionMetrics?.conversionRate}%</Heading>
              <Text>Conversion Rate</Text>
              <Text size="sm" style={{ color: '#525252' }}>
                +2.3% vs last period
              </Text>
            </Stack>
          </Tile>
        </Column>
        <Column sm={2} md={2} lg={2}>
          <Tile>
            <Stack gap={2}>
              <CrowdReport size={24} style={{ color: '#8a3ffc' }} />
              <Heading size="lg">${conversionMetrics?.averageOrderValue}</Heading>
              <Text>Avg Order Value</Text>
              <Text size="sm" style={{ color: '#525252' }}>
                +8.7% vs last period
              </Text>
            </Stack>
          </Tile>
        </Column>
      </Grid>

      {/* Conversion Funnel */}
      <Grid style={{ marginBottom: 24 }}>
        <Column sm={4} md={4} lg={4}>
          <Tile>
            <Heading size="md" style={{ marginBottom: 16 }}>
              Conversion Funnel
            </Heading>
            <DataTable
              headers={[
                { key: 'stage', header: 'Stage' },
                { key: 'count', header: 'Count' },
                { key: 'percentage', header: 'Conversion %' },
              ]}
              rows={conversionFunnelData.map((item, index) => ({
                id: index.toString(),
                stage: item.stage,
                count: item.count.toLocaleString(),
                percentage: `${item.percentage.toFixed(1)}%`,
              }))}
            >
              {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </Tile>
        </Column>
      </Grid>

      {/* A/B Test Results */}
      <Grid style={{ marginBottom: 24 }}>
        <Column sm={4} md={4} lg={4}>
          <Tile>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Heading size="md">A/B Test Performance</Heading>
              <Button kind="ghost" size="sm">
                View All Tests
              </Button>
            </div>
            <DataTable headers={abTestHeaders} rows={abTestRows}>
              {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </Tile>
        </Column>
      </Grid>

      {/* Visitor Behavior Analytics */}
      <Grid style={{ marginBottom: 24 }}>
        <Column sm={2} md={2} lg={2}>
          <Tile>
            <Heading size="md" style={{ marginBottom: 16 }}>
              Visitor Behavior
            </Heading>
            <Stack gap={3}>
              <div>
                <Text weight="semibold">Page Views</Text>
                <Heading size="md">{visitorBehavior?.pageViews.toLocaleString()}</Heading>
              </div>
              <div>
                <Text weight="semibold">Unique Visitors</Text>
                <Heading size="md">{visitorBehavior?.uniqueVisitors.toLocaleString()}</Heading>
              </div>
              <div>
                <Text weight="semibold">Avg Session Duration</Text>
                <Heading size="md">{visitorBehavior?.averageSessionDuration}m</Heading>
              </div>
              <div>
                <Text weight="semibold">Bounce Rate</Text>
                <Heading size="md">{visitorBehavior?.bounceRate}%</Heading>
              </div>
            </Stack>
          </Tile>
        </Column>
        <Column sm={2} md={2} lg={2}>
          <Tile>
            <Heading size="md" style={{ marginBottom: 16 }}>
              Top Performing Pages
            </Heading>
            <Stack gap={2}>
              {visitorBehavior?.topPages.map((page, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>{page.page}</Text>
                  <div>
                    <Text size="sm">{page.views.toLocaleString()} views</Text>
                    <Text size="sm" style={{ color: '#24a148' }}>
                      {page.conversionRate}% CVR
                    </Text>
                  </div>
                </div>
              ))}
            </Stack>
          </Tile>
        </Column>
      </Grid>

      {/* Marketing ROI */}
      <Grid style={{ marginBottom: 24 }}>
        <Column sm={4} md={4} lg={4}>
          <Tile>
            <Heading size="md" style={{ marginBottom: 16 }}>
              Marketing ROI Analysis
            </Heading>
            <Grid>
              <Column sm={2} md={2} lg={2}>
                <Stack gap={3}>
                  <div>
                    <Text weight="semibold">Total Marketing Spend</Text>
                    <Heading size="md">${marketingROI?.totalSpend.toLocaleString()}</Heading>
                  </div>
                  <div>
                    <Text weight="semibold">Total Revenue</Text>
                    <Heading size="md">${marketingROI?.totalRevenue.toLocaleString()}</Heading>
                  </div>
                  <div>
                    <Text weight="semibold">Return on Investment</Text>
                    <Heading
                      size="md"
                      style={{ color: marketingROI && marketingROI.roi > 200 ? '#24a148' : '#da1e28' }}
                    >
                      {marketingROI?.roi}%
                    </Heading>
                  </div>
                </Stack>
              </Column>
              <Column sm={2} md={2} lg={2}>
                <Stack gap={3}>
                  <div>
                    <Text weight="semibold">Cost Per Acquisition</Text>
                    <Heading size="md">${marketingROI?.costPerAcquisition}</Heading>
                  </div>
                  <div>
                    <Text weight="semibold">Customer Lifetime Value</Text>
                    <Heading size="md">${marketingROI?.customerLifetimeValue}</Heading>
                  </div>
                  <div>
                    <Text weight="semibold">Profit Margin</Text>
                    <Heading size="md">{marketingROI?.profitMargin}%</Heading>
                  </div>
                </Stack>
              </Column>
            </Grid>
          </Tile>
        </Column>
      </Grid>

      {/* Export Modal */}
      <Modal
        open={exportModalOpen}
        onRequestClose={() => setExportModalOpen(false)}
        modalHeading="Export Marketing Analytics Report"
        modalLabel="Data Export"
        primaryButtonText="Export"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleExportData}
      >
        <Stack gap={4}>
          <Select
            id="export-format"
            value={exportFormat}
            onChange={e => setExportFormat(e.target.value)}
            labelText="Export Format"
          >
            <SelectItem value="pdf" text="PDF Report" />
            <SelectItem value="excel" text="Excel Spreadsheet" />
            <SelectItem value="csv" text="CSV Data" />
            <SelectItem value="json" text="JSON Data" />
          </Select>

          <Text>
            This report will include conversion metrics, A/B test results, visitor behavior analytics, and ROI
            calculations for the selected time period ({selectedTimeRange.replace('_', ' ')}).
          </Text>
        </Stack>
      </Modal>

      {/* Performance Insights */}
      <Grid>
        <Column sm={4} md={4} lg={4}>
          <Tile>
            <Heading size="md" style={{ marginBottom: 16 }}>
              Performance Insights
            </Heading>
            <Stack gap={3}>
              <InlineNotification
                kind="success"
                title="Strong Performance"
                subtitle={`Conversion rate of ${conversionMetrics?.conversionRate}% exceeds industry average of 8.2%`}
                lowContrast
              />
              <InlineNotification
                kind="info"
                title="A/B Test Recommendation"
                subtitle="Landing page headline test shows significant improvement. Consider implementing winning variant."
                lowContrast
              />
              {marketingROI && marketingROI.roi > 200 && (
                <InlineNotification
                  kind="success"
                  title="Excellent ROI"
                  subtitle={`ROI of ${marketingROI.roi}% indicates highly effective marketing campaigns`}
                  lowContrast
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default MarketingAnalyticsDashboard;
